import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Crown, CheckCircle, Loader2, Gift } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: 'free',
    name: 'Free',
    description: 'Get started — no sign-up needed',
    price: 0,
    features: ['Levels 1-3 unlimited', 'Step-by-step hints', 'Solve walkthroughs', 'Guest play (no email required)'],
    notIncluded: ['Levels 5-17', 'Interactive tutorials', 'Ad-free experience'],
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Full access to everything',
    monthlyPrice: 4.99,
    annualPrice: 49.00,
    lifetimePrice: 89.00,
    icon: Crown,
    iconColor: '#8B5A3C',
    popular: true,
    features: ['All 17 difficulty levels', 'Unlimited puzzles', 'All 17 interactive tutorials', 'Hints & pattern assists', 'Ad-free experience', 'Full statistics', 'Daily challenges', 'Leaderboards', 'Priority support'],
    notIncluded: [],
  },
];

// Extracted Components
const PlanCard = React.memo(function PlanCard({ 
  plan, 
  billingCycle, 
  isCurrentPlan, 
  loading, 
  onSubscribe 
}) {
  const Icon = plan.icon;
  const isCurrent = isCurrentPlan(plan.id);
  
  const price = useMemo(() => {
    if (plan.price === 0) return '$0';
    if (billingCycle === 'lifetime') return `$${plan.lifetimePrice}`;
    return billingCycle === 'monthly' ? `$${plan.monthlyPrice}` : `$${plan.annualPrice}`;
  }, [plan, billingCycle]);
  
  const priceLabel = useMemo(() => {
    if (plan.price === 0) return 'forever';
    if (billingCycle === 'lifetime') return 'one-time · no renewals';
    return billingCycle === 'monthly' ? '/month' : '/year';
  }, [plan.price, billingCycle]);

  const buttonStyle = useMemo(() => {
    if (plan.popular) return 'bg-[#8B5A3C] hover:bg-[#A46944]';
    if (plan.price === 0) return 'bg-[var(--background-elevated)] hover:bg-[var(--background-elevated)] cursor-default';
    return 'bg-[var(--background-elevated)] hover:bg-[var(--hover-bg)]';
  }, [plan.popular, plan.price]);

  const buttonLabel = useMemo(() => {
    if (loading === plan.id) return (
      <span className="flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        Processing...
      </span>
    );
    if (isCurrent) return 'Current Plan';
    if (plan.price === 0) return 'Free Forever';
    return 'Subscribe';
  }, [loading, plan.id, plan.price, isCurrent]);

  return (
    <Card 
      className={`bg-[var(--background-surface)] relative ${
        plan.popular 
          ? 'border-[#8B5A3C] shadow-[0_0_30px_rgba(0,122,255,0.2)]' 
          : 'border-[var(--border-subtle)]'
      }`}
      data-testid={`plan-${plan.id}`}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 rounded-full bg-[#8B5A3C] text-white text-xs font-semibold">
            MOST POPULAR
          </span>
        </div>
      )}
      
      <CardHeader>
        <div className="flex items-center gap-2">
          {Icon && (
            <div 
              className="w-8 h-8 rounded flex items-center justify-center"
              style={{ backgroundColor: `${plan.iconColor}20` }}
            >
              <Icon className="w-4 h-4" style={{ color: plan.iconColor }} />
            </div>
          )}
          <CardTitle className="text-[var(--text-primary)] font-heading">{plan.name}</CardTitle>
        </div>
        <CardDescription className="text-[var(--text-secondary)]">{plan.description}</CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="mb-6">
          <span className="text-4xl font-bold text-[var(--text-primary)]">{price}</span>
          <span className="text-[var(--text-secondary)] ml-1">{priceLabel}</span>
        </div>

        <ul className="space-y-3 mb-6">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-[var(--text-primary)]">
              <CheckCircle className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
              {feature}
            </li>
          ))}
          {plan.notIncluded.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-[var(--text-disabled)]">
              <span className="w-4 h-4 mt-0.5 flex-shrink-0 text-center">-</span>
              {feature}
            </li>
          ))}
        </ul>

        <Button
          onClick={() => onSubscribe(plan.id)}
          disabled={loading === plan.id || isCurrent || plan.price === 0}
          className={`w-full ${buttonStyle} ${plan.popular ? 'text-white' : 'text-[var(--text-primary)]'} font-semibold`}
          data-testid={`subscribe-${plan.id}`}
        >
          {buttonLabel}
        </Button>
      </CardContent>
    </Card>
  );
});

const ExpiryBanner = React.memo(function ExpiryBanner({ user }) {
  // Only render when the user has a paid tier AND an expiry date on record.
  const expires = user?.subscription_expires;
  const tier = user?.subscription_tier;
  const expiryDate = useMemo(() => {
    if (!expires) return null;
    const d = new Date(expires);
    return isNaN(d.getTime()) ? null : d;
  }, [expires]);

  if (!user || !expires || tier === 'free' || !expiryDate) return null;

  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.floor((expiryDate - now) / msPerDay);
  const formatted = expiryDate.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  let variant, label, primary, secondary;
  if (daysLeft < 0) {
    variant = 'expired';
    label = 'Expired';
    primary = `Your Premium expired on ${formatted}.`;
    secondary = 'Renew below or redeem another code to restore access.';
  } else if (daysLeft === 0) {
    variant = 'urgent';
    label = 'Expires today';
    primary = `Your Premium expires today (${formatted}).`;
    secondary = 'Renew now to keep access to Levels 5–17 without interruption.';
  } else if (daysLeft <= 14) {
    variant = 'warn';
    label = `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;
    primary = `Your Premium expires on ${formatted}.`;
    secondary = 'Renew now or redeem another code to avoid interruption.';
  } else {
    variant = 'ok';
    label = `${daysLeft} days left`;
    primary = `Your Premium is active through ${formatted}.`;
    secondary = null;
  }

  const styles = {
    ok:      { bg: 'rgba(52, 199, 89, 0.08)', border: 'rgba(52, 199, 89, 0.35)', accent: '#34C759' },
    warn:    { bg: 'rgba(255, 149, 0, 0.10)', border: 'rgba(255, 149, 0, 0.45)', accent: '#FF9500' },
    urgent:  { bg: 'rgba(255, 59, 48, 0.10)', border: 'rgba(255, 59, 48, 0.50)', accent: '#FF3B30' },
    expired: { bg: 'rgba(255, 59, 48, 0.14)', border: 'rgba(255, 59, 48, 0.60)', accent: '#FF3B30' },
  }[variant];

  return (
    <div
      className="max-w-3xl mx-auto mt-8 rounded-lg px-5 py-4 flex items-start gap-3"
      style={{ backgroundColor: styles.bg, borderWidth: '1px', borderStyle: 'solid', borderColor: styles.border }}
      data-testid="expiry-banner"
      data-variant={variant}
    >
      <span
        className="inline-flex items-center justify-center text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0"
        style={{ backgroundColor: styles.accent, color: '#fff', letterSpacing: '0.03em' }}
      >
        {label}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">{primary}</p>
        {secondary && (
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">{secondary}</p>
        )}
      </div>
    </div>
  );
});


const PromoRedeemCard = React.memo(function PromoRedeemCard({ user, onRedeemed, navigate }) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleRedeem = useCallback(async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error('Enter a promo code');
      return;
    }
    if (!user) {
      toast.info('Please sign in to redeem a promo code');
      navigate('/login');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/promo/redeem`,
        { code: trimmed },
        { withCredentials: true }
      );
      toast.success(data.message || 'Premium activated!');
      setCode('');
      if (onRedeemed) await onRedeemed();
    } catch (err) {
      // Common case: cookie expired or wasn't sent (cross-domain). Surface
      // a clearer message and bounce to login so the user can re-auth.
      if (err.response?.status === 401) {
        toast.error('Your session expired — please sign in again');
        navigate('/login');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to redeem promo code');
      }
    } finally {
      setSubmitting(false);
    }
  }, [code, user, onRedeemed, navigate]);

  return (
    <Card
      className="bg-[var(--background-surface)] border-[var(--border-subtle)] max-w-3xl mx-auto mt-8"
      data-testid="promo-redeem-card"
    >
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded flex items-center justify-center bg-[#FF9F0A]/20">
            <Gift className="w-4 h-4 text-[#FF9F0A]" />
          </div>
          <CardTitle className="text-[var(--text-primary)] font-heading">Have a Promo Code?</CardTitle>
        </div>
        <CardDescription className="text-[var(--text-secondary)]">
          Enter your code to instantly activate Premium access.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleRedeem} className="flex flex-col sm:flex-row gap-3">
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ENTER PROMO CODE"
            className="flex-1 bg-[var(--background-main)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono tracking-wider"
            maxLength={32}
            data-testid="promo-code-input"
          />
          <Button
            type="submit"
            disabled={submitting || !code.trim()}
            className="bg-[#FF9F0A] hover:bg-[#FFB340] text-black font-semibold"
            data-testid="promo-redeem-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Redeeming...
              </span>
            ) : (
              'Redeem'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
});

export default function SubscriptionPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  // Fetch subscription
  useEffect(() => {
    if (!user) return;
    
    let mounted = true;
    const fetchSubscription = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/subscription/me`, { withCredentials: true });
        if (mounted) setCurrentSubscription(data);
      } catch (err) {
        toast.error('Failed to load subscription status');
      }
    };
    
    fetchSubscription();
    return () => { mounted = false; };
  }, [user]);

  // Handle Stripe return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (!sessionId) return;

    let mounted = true;
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const pollStatus = async () => {
      if (!mounted || attempts >= maxAttempts) {
        if (attempts >= maxAttempts) {
          toast.error('Payment status check timed out. Please refresh the page.');
        }
        return;
      }

      try {
        const { data } = await axios.get(`${API_URL}/api/payments/status/${sessionId}`, { withCredentials: true });
        
        if (data.payment_status === 'paid') {
          toast.success('Payment successful! Welcome to Premium!');
          await checkAuth();
          navigate('/subscription', { replace: true });
          return;
        } else if (data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          navigate('/subscription', { replace: true });
          return;
        }

        attempts++;
        setTimeout(pollStatus, pollInterval);
      } catch (err) {
        toast.error('Error checking payment status');
      }
    };

    pollStatus();
    return () => { mounted = false; };
  }, [checkAuth, navigate]);

  const handleSubscribe = useCallback(async (tier) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (tier === 'free') return;

    setLoading(tier);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { tier, billing_cycle: billingCycle, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  }, [user, navigate, billingCycle]);

  const isCurrentPlan = useCallback((planId) => {
    const tier = currentSubscription?.tier;
    if (planId === 'premium') return tier === 'premium' || tier === 'premium_plus';
    return tier === planId;
  }, [currentSubscription?.tier]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-4">
            Choose Your Plan
          </h1>
          <p className="text-[var(--text-secondary)] text-lg max-w-2xl mx-auto mb-8">
            Unlock all 17 difficulty levels, interactive tutorials, and compete on leaderboards.
          </p>

          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-[var(--background-surface)] border border-[var(--border-subtle)] flex-wrap justify-center">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly' 
                  ? 'bg-[#8B5A3C] text-white' 
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'annual' 
                  ? 'bg-[#8B5A3C] text-white' 
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
              data-testid="billing-annual"
            >
              Annual
              <span className="ml-2 text-xs text-[#34C759]">Save 18%</span>
            </button>
            <button
              onClick={() => setBillingCycle('lifetime')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'lifetime'
                  ? 'bg-[#AF52DE] text-white'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
              data-testid="billing-lifetime"
            >
              Lifetime
              <span className="ml-2 text-xs text-[#FF9500]">No renewals</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              billingCycle={billingCycle}
              isCurrentPlan={isCurrentPlan}
              loading={loading}
              onSubscribe={handleSubscribe}
            />
          ))}
        </div>

        <ExpiryBanner user={user} />

        <PromoRedeemCard user={user} onRedeemed={checkAuth} navigate={navigate} />

        <div className="mt-16 text-center space-y-2">
          <p className="text-[var(--text-disabled)] text-sm">
            All plans include unlimited Naked Single and Hidden Single puzzles.
            Cancel anytime — cancellations are not prorated and no refunds for unused time. Prices in USD.
          </p>
          <p className="text-[var(--text-disabled)] text-xs">
            Lifetime is a single one-time payment with no recurring charges, no auto-renewal, and no expiry — keep access for the lifetime of the service.
          </p>
        </div>
      </main>
    </div>
  );
}
